import { useCallback, useEffect, useRef } from 'react';
import { GoogleMap, Marker, OverlayView, OVERLAY_MOUSE_TARGET } from '@react-google-maps/api';
import { GOOGLE_MAP_STYLES } from '@/pages/dashboard/googleMapStyles';
import {
  MAP_FIT_BOUNDS_PADDING,
  MAP_FOCUS_MIN_ZOOM,
  MAP_INIT_DELAY_MS,
  MAP_SINGLE_MARKER_ZOOM,
} from '@/pages/dashboard/constants/map.constants';
import { GOOGLE_MAPS_API_KEY, INDIA_MAP_CENTER, INDIA_MAP_ZOOM } from '@/config/googleMaps';
import type { MapMarker } from '@/utils/dashboardMap';
import { createMapPinIcon, getMapMarkerKey } from '@/utils/dashboardMap';
import {
  GOOGLE_MAP_CONTAINER_STYLE,
  getMapPopupOffset,
  triggerMapResize,
} from '@/utils/googleMapHelpers';
import { MapMarkerPopup } from './MapMarkerPopup';
import styles from '../../Dashboard.module.scss';

interface GoogleOrdersMapProps {
  markers: MapMarker[];
  activeKey: string | null;
  onMarkerSelect: (marker: MapMarker) => void;
}

export function GoogleOrdersMap({ markers, activeKey, onMarkerSelect }: GoogleOrdersMapProps) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const lastPannedKey = useRef<string | null>(null);

  const activeMarker = markers.find((marker) => getMapMarkerKey(marker) === activeKey) ?? null;

  const fitAllMarkers = useCallback(() => {
    if (!mapRef.current || markers.length === 0) return;

    if (markers.length === 1) {
      mapRef.current.setCenter({ lat: markers[0].lat, lng: markers[0].lng });
      mapRef.current.setZoom(MAP_SINGLE_MARKER_ZOOM);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    markers.forEach((marker) => bounds.extend({ lat: marker.lat, lng: marker.lng }));
    mapRef.current.fitBounds(bounds, MAP_FIT_BOUNDS_PADDING);
  }, [markers]);

  const panToMarker = useCallback((marker: MapMarker) => {
    if (!mapRef.current) return;

    mapRef.current.panTo({ lat: marker.lat, lng: marker.lng });
    const zoom = mapRef.current.getZoom() ?? INDIA_MAP_ZOOM;
    if (zoom < MAP_FOCUS_MIN_ZOOM) {
      mapRef.current.setZoom(MAP_FOCUS_MIN_ZOOM);
    }
  }, []);

  useEffect(() => {
    if (!activeMarker || !mapRef.current) return;

    const markerKey = getMapMarkerKey(activeMarker);
    if (lastPannedKey.current === markerKey) return;

    lastPannedKey.current = markerKey;
    panToMarker(activeMarker);
  }, [activeMarker, panToMarker]);

  useEffect(() => {
    lastPannedKey.current = null;
    if (!mapRef.current) return;
    fitAllMarkers();
  }, [markers, fitAllMarkers]);

  return (
    <GoogleMap
      mapContainerStyle={GOOGLE_MAP_CONTAINER_STYLE}
      center={INDIA_MAP_CENTER}
      zoom={INDIA_MAP_ZOOM}
      options={{
        styles: GOOGLE_MAP_STYLES,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: true,
        zoomControl: true,
        gestureHandling: 'greedy',
      }}
      onLoad={(map) => {
        mapRef.current = map;
        triggerMapResize(map);
        window.setTimeout(() => {
          fitAllMarkers();
          triggerMapResize(map);
        }, MAP_INIT_DELAY_MS);
      }}
    >
      {markers.map((marker) => {
        const markerKey = getMapMarkerKey(marker);
        const isActive = activeKey === markerKey;

        return (
          <Marker
            key={markerKey}
            position={{ lat: marker.lat, lng: marker.lng }}
            icon={createMapPinIcon(marker.orders, isActive)}
            zIndex={isActive ? 1000 : marker.orders}
            onClick={() => onMarkerSelect(marker)}
          />
        );
      })}

      {activeMarker ? (
        <OverlayView
          position={{ lat: activeMarker.lat, lng: activeMarker.lng }}
          mapPaneName={OVERLAY_MOUSE_TARGET}
          getPixelPositionOffset={getMapPopupOffset}
        >
          <MapMarkerPopup marker={activeMarker} />
        </OverlayView>
      ) : null}
    </GoogleMap>
  );
}

interface MapCanvasProps {
  markers: MapMarker[];
  activeKey: string | null;
  isLoaded: boolean;
  loadError: Error | undefined;
  onMarkerSelect: (marker: MapMarker) => void;
}

export function MapCanvas({
  markers,
  activeKey,
  isLoaded,
  loadError,
  onMarkerSelect,
}: MapCanvasProps) {
  if (markers.length === 0) {
    return (
      <div className={styles.mapEmpty}>
        No location data for this period. Orders appear once shipping cities are recorded.
      </div>
    );
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className={styles.mapEmpty}>
        Add <code>VITE_GOOGLE_MAPS_API_KEY</code> to your admin <code>.env</code> file to enable
        Google Maps.
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.mapEmpty}>
        Unable to load Google Maps. Check your API key and billing settings.
      </div>
    );
  }

  if (!isLoaded) {
    return <div className={styles.mapLoading}>Loading Google Maps…</div>;
  }

  return (
    <div className={styles.googleMapWrap}>
      <GoogleOrdersMap markers={markers} activeKey={activeKey} onMarkerSelect={onMarkerSelect} />
    </div>
  );
}
